import {
  type ByAddOnSelected,
  type ByMultiplierDeselected,
  type ByMultiplierSelected,
  type ByMultiplierValueMatched,
  type PrizeTierCategoryRequirements,
  PrizeTierCategoryRequirementsPayloadType,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import type { Config } from '../@types/editor'

const DEFAULT_CATEGORY_NAME = 'DEFAULT'

const DEFAULT_GROUP = 'DEFAULT'

const ADD_ON_NAME_TYPES = [
  PrizeTierCategoryRequirementsPayloadType.ByAddonSelected,
  PrizeTierCategoryRequirementsPayloadType.ByMultiplierDeselected,
  PrizeTierCategoryRequirementsPayloadType.ByMultiplierSelected,
  PrizeTierCategoryRequirementsPayloadType.ByMultiplierValueMatched,
]

const MULTIPLIER_TYPES = [
  PrizeTierCategoryRequirementsPayloadType.ByMultiplierSelected,
  PrizeTierCategoryRequirementsPayloadType.ByMultiplierValueMatched,
]

const hasAddOnName = (
  requirement: PrizeTierCategoryRequirements,
): requirement is
  | ByAddOnSelected
  | ByMultiplierDeselected
  | ByMultiplierSelected
  | ByMultiplierValueMatched => ADD_ON_NAME_TYPES.includes(requirement.type)

const isMultiplierRequirement = (
  requirement: PrizeTierCategoryRequirements,
): requirement is ByMultiplierSelected | ByMultiplierValueMatched =>
  MULTIPLIER_TYPES.includes(requirement.type)

export const hasPrizeTiers = (config: Maybe<Config>) =>
  (config?.prizeTierCategories ?? []).some(
    ({ group, prizeTiers }) => group === DEFAULT_GROUP && prizeTiers.length > 0,
  )

export const useEditorPrizeTiers = (config: MaybeRefOrGetter<Maybe<Config>>) => {
  const currentConfig = computed(() => toValue(config))

  const categories = computed(() =>
    (currentConfig.value?.prizeTierCategories ?? []).filter(({ group }) => group === DEFAULT_GROUP),
  )

  const multiplierAddOn = computed(
    () =>
      [
        ...(currentConfig.value?.addOns ?? []),
        ...(currentConfig.value?.afterPurchaseAddOns ?? []),
      ].find(({ perPlay }) => !perPlay) ?? null,
  )

  const tierMultipliers = computed(() =>
    categories.value.reduce<string[]>((acc, { categoryRequirements, name }, index) => {
      if (name === DEFAULT_CATEGORY_NAME) return [...acc, '']

      if (multiplierAddOn.value?.options.length) {
        return [...acc, multiplierAddOn.value.options[index - 1] ?? '']
      }

      return [
        ...acc,
        ...categoryRequirements.filter(isMultiplierRequirement).map(({ value }) => String(value)),
      ]
    }, []),
  )

  const tabs = computed(() =>
    categories.value.map(({ categoryRequirements, name, prizeTiers }, index) => {
      const [firstRequirement] = categoryRequirements

      return {
        data: {
          addOnName:
            firstRequirement && hasAddOnName(firstRequirement)
              ? firstRequirement.addOnName
              : multiplierAddOn.value?.name,
          prizeTiers,
          tierMultiplier: tierMultipliers.value[index],
        },
        name,
      }
    }),
  )

  return { tabs }
}
